// The CTA library: one list of call-to-action buttons, referenced from funnels,
// website sections and posts rather than retyped into each of them.
//
// The whole point is that a CTA is referenced, not owned — edit it once and it
// changes everywhere it is used. That is why this is a store of its own rather
// than a field on each thing that points at one.

import { createRecordStore } from "./recordStore";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
// `name` is internal (how she finds it); `buttonText` is what the public reads.
// They are deliberately separate: "Archetype quiz - top of funnel" is a useful
// name and a terrible button.
// `description` exists for the model, not the UI: a button label is an instruction,
// not meaning. "Take the assessment" tells a writer nothing about what the
// assessment is or who it's for, so a post can only bolt it on at the end. One
// sentence of what it actually is turns the CTA into something a post can build
// toward.
export const CTA_FIELDS = ["name", "buttonText", "description", "link", "type", "status"];

/** What the model should be told this CTA is. Falls back through the fields that
 *  carry meaning, so a half-filled CTA still produces a usable instruction rather
 *  than silently becoming no CTA at all. */
export function ctaBrief(cta) {
  if (!cta) return null;
  /* Trim BEFORE choosing, not after. `(buttonText || name).trim()` looks
     equivalent and isn't: a whitespace-only buttonText is truthy, so `||` picks
     it, trims to empty, and the whole CTA silently becomes null — the same
     no-ask-at-all failure this function exists to prevent. */
  const label = String(cta.buttonText || "").trim() || String(cta.name || "").trim();
  if (!label) return null;
  const what = (cta.description || "").trim();
  return { label, what: what || null };
}

/** What to call a CTA on screen. The name is how she finds it; button text is the
 *  fallback because a CTA can be saved before it has been named. */
export function ctaLabel(cta) {
  return String(cta?.name || "").trim() || String(cta?.buttonText || "").trim() || "Untitled link";
}

/** What picking a GHL calendar changes on a meeting link (2026-10-09): the
 *  link, and the type — it is a booking link now. Her name and her "What is
 *  it?" are never overwritten; a calendar's only fill them while empty. */
export function calendarPatch(draft, cal) {
  return {
    link: cal.link,
    type: "call",
    ...(String(draft?.name || "").trim() ? {} : { name: cal.name }),
    ...(String(draft?.description || "").trim() || !cal.description ? {} : { description: cal.description }),
  };
}

export const useCtas = createRecordStore({
  table: "ctas",
  fields: CTA_FIELDS,
  idPrefix: "cta",
});

/* Where a CTA sends people. The picker rewrites the form's explanatory copy, so
   these carry their own words rather than the form branching on a bare string. */
export const CTA_TYPES = [
  { id: "landing", icon: "🔗", label: "Landing page",
    hint: "Links to a page you host elsewhere — GHL, your CRM, or your website.",
    linkLabel: "Landing page link", placeholder: "info.tevrawood.com/archetype-assessment",
    linkHint: "Brill doesn't host pages — paste the live URL from GHL, your CRM, or your website.",
    defaultButton: "Start now" },
  { id: "call", icon: "📅", label: "Booking link",
    hint: "Sends people straight to your booking calendar.",
    linkLabel: "Booking link", placeholder: "https://calendly.com/you/clarity-call",
    linkHint: "Paste your Calendly or GHL booking URL.",
    defaultButton: "Book now" },
  { id: "url", icon: "🌐", label: "External tool",
    hint: "Any other link — a checkout, an application, anywhere.",
    linkLabel: "Destination URL", placeholder: "https://…",
    linkHint: "Any web address you want this link to open.",
    defaultButton: "Go" },
];

export function ctaType(id) {
  return CTA_TYPES.find((t) => t.id === id) || CTA_TYPES[2];
}

/**
 * Status answers "does the link work", NOT "is the copy finished".
 *
 * That distinction is the whole reason this is derived rather than stored: a CTA
 * with no URL is broken wherever it is already referenced, and calling that
 * "Draft" hides it behind a word that sounds deliberate. She cannot mark a
 * link-less CTA live, because it isn't.
 */
export function ctaStatus(cta) {
  if (!cta?.link || !String(cta.link).trim()) return "no_url";
  return cta.status === "live" ? "live" : "draft";
}

export const CTA_STATUS_LABEL = { live: "Live", draft: "Draft", no_url: "No URL yet" };

/**
 * What currently points at this CTA.
 *
 * Offers are the first real consumer — each rung of the ladder names the CTA that
 * sends people to it. Funnels, website sections and social posts join this as
 * they land, which is why it takes a bag of sources rather than one list, and why
 * `total` is derived rather than being the offer count under another name.
 */
export function ctaUses(ctaId, { offers = [] } = {}) {
  const offerCount = ctaId ? (offers || []).filter((o) => o.ctaId === ctaId).length : 0;
  return { offers: offerCount, total: offerCount };
}

/** One line for the "Used in" column. Null means nothing points at it yet — the
 *  caller decides how to word that, since the table and the problem summary say
 *  it differently. */
export function ctaUseSummary(uses) {
  const parts = [];
  if (uses?.offers) parts.push(`${uses.offers} offer${uses.offers === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * What's wrong with the library right now, for the summary line above the table.
 * Counting problems rather than listing everything means the line stays quiet
 * when there is nothing to say.
 *
 * `usedCount` is passed in rather than read here: the caller knows which sources
 * it has loaded. The library passes `ctaUses(...).total`; the default of zero is
 * for callers with nothing loaded, not a claim that nothing references CTAs.
 */
export function ctaProblems(ctas, usedCount = () => 0) {
  const rows = Array.isArray(ctas) ? ctas : [];
  return {
    noUrl: rows.filter((c) => ctaStatus(c) === "no_url").length,
    unused: rows.filter((c) => usedCount(c) === 0).length,
  };
}

/** Human summary of the above, or null when the library is healthy. */
export function ctaProblemSummary(problems) {
  const parts = [];
  if (problems.noUrl) parts.push(`${problems.noUrl} link${problems.noUrl === 1 ? " has" : "s have"} no URL`);
  if (problems.unused) parts.push(`${problems.unused} not used anywhere`);
  return parts.length ? parts.join(" · ") : null;
}
